// eslint-disable-next-line no-use-before-define
import React, { useState, useEffect } from 'react';
import { SendBirdProvider } from '@sendbird/uikit-react';
import sendbirdStyles from '@sendbird/uikit-react/dist/index.css?raw';

import { VueInReact } from 'vuera-ts';
import ChannelListWrapper from './components/channel_list/ChannelListWrapper';
import ChannelWrapper from './components/channel/ChannelWrapper';
import AlertBanner from '../../components/alert_banner/AlertBanner.vue';
import UpgradePrompt from './components/UpgradePrompt.vue';
import HelperStatus from '../../helpers/status';
import useMediaQuery from './js/useMediaQuery.js';
import { sendSegmentEvent } from '../../lib/segment';
import { waitForElm } from './js/addAccessibility';
import { selectQueryParamChannel } from './js/chatLogic';

const AlertBannerReact = VueInReact(AlertBanner);
const UpgradePromptReact = VueInReact(UpgradePrompt);

const PANELS = {
  CHANNEL_LIST: 'CHANNEL_LIST',
  CHANNEL: 'CHANNEL',
};

const SEND_BIRD_STYLE_ID = 'sendbird-uikit-styles';

const ensureStylesheetLink = (id, href) => {
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

const ensureSendbirdStyles = () => {
  if (document.getElementById(SEND_BIRD_STYLE_ID)) return;

  // Sendbird ships invalid @import ordering in dist CSS; inject as raw text to avoid Vite CSS parser errors.
  const style = document.createElement('style');
  style.id = SEND_BIRD_STYLE_ID;
  style.textContent = sendbirdStyles;
  document.head.appendChild(style);

  ensureStylesheetLink('sendbird-font-open-sans', 'https://fonts.googleapis.com/css?family=Open+Sans:400,600,700&display=swap');
  ensureStylesheetLink('sendbird-font-roboto', 'https://fonts.googleapis.com/css?family=Roboto:400,500,600,700&display=swap');
};

export default function Chat({ userId, appId, token, accountType, scColorSet, hasBasicAccount }) {
  const [currentChannelUrl, setCurrentChannelUrl] = useState(null);
  const [currentChannelMembers, setCurrentChannelMembers] = useState(null);
  const [channelCount, setChannelCount] = useState(0);
  const [allChannels, setAllChannels] = useState([]);
  const [queryParamChannelNotFound, setQueryParamChannelNotFound] = useState(false);
  const [panel, setPanel] = useState(PANELS.CHANNEL_LIST);
  const [hasError, setHasError] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const uuidParam = urlParams.get('uuid');

  const changeToChannelPannel = () => setPanel(PANELS.CHANNEL);
  const updateChannelNotFound = () => setQueryParamChannelNotFound(true);
  const updateCurrentChannelUrl = (channelurl) => setCurrentChannelUrl(channelurl);
  const updateCurrentChannelMembers = (members) => setCurrentChannelMembers(members);

  useEffect(() => {
    selectQueryParamChannel(
      uuidParam,
      allChannels,
      updateCurrentChannelUrl,
      updateCurrentChannelMembers,
      changeToChannelPannel,
      updateChannelNotFound,
      sendSegmentEvent
    );
  }, [JSON.stringify(allChannels)]);

  useEffect(() => {
    if (panel === PANELS.CHANNEL_LIST && currentChannelUrl) {
      waitForElm(`#${currentChannelUrl}`).then(() => {
        document.getElementById(currentChannelUrl)?.scrollIntoView();
      });
    }
  }, [panel]);

  useEffect(() => {
    ensureSendbirdStyles();
  }, []);

  const mobile = useMediaQuery('(max-width: 767px)');

  // prevent multiline textbox height from persisting across channels
  const removeTextboxStyles = () => {
    const textbox = document.querySelector('.sendbird-message-input--textarea');
    textbox?.removeAttribute('style');
  };

  const handleChannelSelect = (channel) => {
    if (channel && channel.url && channel.members) {
      setCurrentChannelUrl(channel.url);
      setCurrentChannelMembers(channel.members);
      if (mobile) setPanel(PANELS.CHANNEL);
      removeTextboxStyles();
    }
  };

  const handleMobileNav = () => {
    const mobileNavBar = document.getElementById('mobile-nav-bar');

    if (mobileNavBar) {
      // eslint-disable-next-line no-unused-expressions
      panel === PANELS.CHANNEL ? mobileNavBar.classList.add('hidden') : mobileNavBar.classList.remove('hidden');
    }
  };
  handleMobileNav();

  const updateErrorState = () => {
    setHasError(true);
  };

  const updateAllChannels = (newChannels) => {
    if (JSON.stringify(newChannels) !== JSON.stringify(allChannels)) {
      if (newChannels.length) {
        setAllChannels(newChannels);
        setChannelCount(newChannels.length);
      }
    }
  };

  // calculating inner height since mobile vh is different from web vh
  const appHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  window.addEventListener('resize', appHeight);
  appHeight();

  return (
    <div className="sendbird-messaging-page">
      {hasError ? (
        <AlertBannerReact status={HelperStatus.Warn}>
          There was an error loading your messages. Try reloading or contact{' '}
          <a href="https://support.sittercity.com/hc/en-us" target="_blank">
            Customer Success
          </a>
          .
        </AlertBannerReact>
      ) : null}
      <SendBirdProvider
        breakpoint="767px"
        mediaQueryBreakPoint={mobile}
        appId={appId}
        userId={userId}
        accessToken={token}
        colorSet={scColorSet}
        onUserProfileMessage={(channel) => {
          setCurrentChannel(channel);
        }}
      >
        {hasBasicAccount && channelCount > 0 ? <UpgradePromptReact /> : null}
        {mobile ? (
          <div className="sendbird-app__wrap">
            {panel === PANELS.CHANNEL_LIST && (
              <ChannelListWrapper
                onChannelSelect={handleChannelSelect}
                userId={userId}
                accountType={accountType}
                disableAutoSelect={true}
                updateErrorState={updateErrorState}
                updateAllChannels={updateAllChannels}
                hasError={hasError}
                currentChannelUrl={currentChannelUrl}
              />
            )}
            {panel === PANELS.CHANNEL && (
              <ChannelWrapper
                currentChannelUrl={currentChannelUrl}
                currentChannelMembers={currentChannelMembers}
                userId={userId}
                accountType={accountType}
                hasBasicAccount={hasBasicAccount}
                queryParamChannelNotFound={queryParamChannelNotFound}
                onBackClick={() => {
                  setPanel(PANELS.CHANNEL_LIST);
                }}
              />
            )}
          </div>
        ) : (
          <div className="sendbird-app__wrap">
            <ChannelListWrapper
              onChannelSelect={handleChannelSelect}
              userId={userId}
              accountType={accountType}
              disableAutoSelect={!!uuidParam}
              updateErrorState={updateErrorState}
              updateAllChannels={updateAllChannels}
              hasError={hasError}
              currentChannelUrl={currentChannelUrl}
            />
            <ChannelWrapper
              currentChannelUrl={currentChannelUrl}
              currentChannelMembers={currentChannelMembers}
              userId={userId}
              accountType={accountType}
              hasBasicAccount={hasBasicAccount}
              queryParamChannelNotFound={queryParamChannelNotFound}
            />
          </div>
        )}
      </SendBirdProvider>
    </div>
  );
}
